import { Graph } from "@components/HorizontalChart/chart.types";

export const customerJourneyData: Graph = {
  nodes: [
    // Step 1
    { name: "Attribution sales", depth: 0, value: 2000 },

    // Step 1.33
    { name: "DSP", depth: 0.5, value: 600 },

    // Step 1.66
    { name: "Search Volume", depth: 1.3, value: 600 },

    // Step 2
    { name: "Detailed page view", depth: 2.45, value: 2600 },
    { name: "Drop Off", depth: 2.45, value: 600 },

    // Step 3
    { name: "Add-to-gift-list", depth: 3.6, value: 200 },
    { name: "Add-to-wish-list", depth: 3.6, value: 500 },
    { name: "Add to cart", depth: 3.6, value: 900 },
    { name: "Drop Off Step 3", depth: 3.6, value: 400 },

    // Step 4
    { name: "Orders", depth: 4.8, value: 1050 },
    { name: "Drop Off Step 4", depth: 4.8, value: 400 },

    // Step 5
    { name: "Subscribe and Save First Order", depth: 6, value: 400 },
    { name: "Subscribe and Save Repeat Order", depth: 6, value: 200 },
    { name: "One Time Order", depth: 6, value: 450 },
  ],
  links: [
    // Attribution sales
    { source: "Attribution sales", target: "Detailed page view", value: 2000 },

    // DSP
    { source: "DSP", target: "Search Volume", value: 200 },
    { source: "DSP", target: "Detailed page view", value: 200 },
    { source: "DSP", target: "Drop Off", value: 200 },

    // Search Volume
    { source: "Search Volume", target: "Detailed page view", value: 400 },
    { source: "Search Volume", target: "Drop Off", value: 400 },

    // From Detailed page view
    { source: "Detailed page view", target: "Add-to-gift-list", value: 200 },
    { source: "Detailed page view", target: "Add-to-wish-list", value: 300 },
    { source: "Detailed page view", target: "Add to cart", value: 900 },
    { source: "Detailed page view", target: "Drop Off Step 3", value: 400 },

    // To Orders
    { source: "Add-to-gift-list", target: "Orders", value: 150 },
    { source: "Add-to-wish-list", target: "Orders", value: 200 },
    { source: "Add to cart", target: "Orders", value: 700 },
    { source: "Add to cart", target: "Drop Off Step 4", value: 200 },
    { source: "Add-to-wish-list", target: "Drop Off Step 4", value: 200 },

    // Final conversions
    { source: "Orders", target: "Subscribe and Save First Order", value: 400 },
    { source: "Orders", target: "Subscribe and Save Repeat Order", value: 200 },
    { source: "Orders", target: "One Time Order", value: 450 },
  ],
};

// Function to generate audience paths from customerJourneyData
export const generateAudiencePaths = (
  data: Graph,
): { [key: string]: number } => {
  const paths: { [key: string]: number } = {};

  const findAllPaths = (
    currentNode: string,
    targetNode: string,
    visited: Set<string>,
    currentPath: string[],
    allPaths: { path: string[]; value: number }[],
  ) => {
    if (currentNode === targetNode) {
      allPaths.push({ path: [...currentPath], value: 0 });
      return;
    }

    visited.add(currentNode);

    const outgoingLinks = data.links.filter(
      (link) => link.source === currentNode,
    );

    for (const link of outgoingLinks) {
      if (!visited.has(link.target)) {
        findAllPaths(
          link.target,
          targetNode,
          new Set(visited),
          [...currentPath, link.target],
          allPaths,
        );
      }
    }
  };

  const allNodes = new Set([
    ...data.nodes.map((node) => node.name),
    ...data.links.map((link) => link.source),
    ...data.links.map((link) => link.target),
  ]);

  data.links.forEach((link) => {
    const pathKey = `${link.source} → ${link.target}`;
    paths[pathKey] = link.value;
  });

  const startNodes = ["Attribution sales", "DSP", "Search Volume"];
  const endNodes = [
    "Drop Off",
    "Drop Off Step 3",
    "Drop Off Step 4",
    "Subscribe and Save First Order",
    "Subscribe and Save Repeat Order",
    "One Time Order",
  ];

  startNodes.forEach((startNode) => {
    endNodes.forEach((endNode) => {
      const allPaths: { path: string[]; value: number }[] = [];
      findAllPaths(startNode, endNode, new Set(), [startNode], allPaths);

      allPaths.forEach((pathObj) => {
        if (pathObj.path.length > 2) {
          const pathKey = pathObj.path.join(" → ");
          // Calculate path value as minimum of all links in the path
          let pathValue = Infinity;
          for (let i = 0; i < pathObj.path.length - 1; i++) {
            const link = data.links.find(
              (l) =>
                l.source === pathObj.path[i] &&
                l.target === pathObj.path[i + 1],
            );
            if (link) {
              pathValue = Math.min(pathValue, link.value);
            }
          }
          if (pathValue !== Infinity) {
            paths[pathKey] = pathValue;
          }
        }
      });
    });
  });

  return paths;
};

// Simplified table columns
export const customerJourneyColumns = [
  {
    title: "Audience",
    dataIndex: "path",
    key: "path",
    width: 600,
    fixed: "left",
  },
  {
    title: "Audience Size",
    dataIndex: "audienceSize",
    key: "audienceSize",
    width: 150,
    render: (value: number) => value.toLocaleString(),
  },
];
